import { useEffect, useMemo, useRef } from 'react';
import { EventMembership } from '@shared/components/AuthProvider/AuthProvider.reducer';
import { EventUserStatus } from '@graphql/generated';

export type UseAutoAssociateUserToEventArgs = {
  hasAuthenticatedOnce: boolean;
  initialEventId: Maybe<string>;
  eventId: Maybe<string>;
  eventMemberships: EventMembership[];
  onSingleMatch: (membership: EventMembership) => void;
  onZeroOrMultipleMatches: (memberships: EventMembership[]) => void;
};

/**
 * When possible, automatically associate user when:
 *  - initialEventId is provided and valid
 *  - user has not selected an event but has a valid usable event membership
 */
export const useAutoAssociateUserToEvent = ({
  hasAuthenticatedOnce,
  eventId,
  eventMemberships,
  initialEventId,
  onZeroOrMultipleMatches,
  onSingleMatch
}: UseAutoAssociateUserToEventArgs): void => {
  const eventIdRef = useRef(initialEventId || null);
  const ownedOrAdminOfEvents = useMemo(() => eventMemberships.filter(event => event.status === EventUserStatus.owner || event.status === EventUserStatus.admin), [
    eventMemberships
  ]);

  useEffect(() => {
    if (!hasAuthenticatedOnce) {
      // Can only run after authentication
      return;
    }

    if (eventIdRef.current !== null) {
      // See if the provided eventId is valid
      const idToMatchAgainst = eventIdRef.current;
      // Always nullify the stored value
      eventIdRef.current = null;

      const eventMembership = ownedOrAdminOfEvents.find(membership => membership.event?.id === idToMatchAgainst);
      if (eventMembership) {
        // Event id is valid, set id and exit early
        onSingleMatch(eventMembership);
        return;
      }
    }

    if (!eventId) {
      if (ownedOrAdminOfEvents.length === 1) {
        // Automatically select the only owned/admin event
        onSingleMatch(ownedOrAdminOfEvents[0]);
      } else {
        onZeroOrMultipleMatches(ownedOrAdminOfEvents);
      }
    }
  }, [eventId, hasAuthenticatedOnce, ownedOrAdminOfEvents, onZeroOrMultipleMatches, onSingleMatch]);
};
